/* Mode-neutral study rewards. Learning owners emit accepted actions; this module never
   changes answers, scores, FSRS or schedules. Core commits receipts, XP and wallet together. */
(function () {
    'use strict';
    const GAME_MODE_SOURCES = Object.freeze({
        quizzes: { route: 'learn-quizzes', label: 'Quizzes', unit: 'answers', points: 10, xp: 15, hook: 'handleChoice' },
        exam: { label: 'Exam', unit: 'answers', points: 10, xp: 15, hook: 'finishNativeFeedback' },
        flashcards: { route: 'learn-flashcards', label: 'Flashcards', unit: 'cards', points: 2, xp: 3, hook: 'setKnowledge', mastery: 100 },
        guide: { route: 'learn-guide', label: 'Study Guide', unit: 'min of reading', points: 10, xp: 15, hook: 'finishMemoraStudyGuideSectionQuiz', mastery: 10 },
        clinic: { route: 'learn-clinic', label: 'Clinic', unit: 'decisions', points: 10, xp: 15, hook: 'clinicSubmitAnswer', mastery: 10 },
        occlusion: { route: 'learn-occlusion', label: 'Closed Occlusion', unit: 'cards', points: 2, xp: 3, hook: 'gradePdfTutorOcclusionBox', mastery: 100 },
        guided: { route: 'learn-guided', label: 'Guided', unit: 'answers', points: 10, xp: 15, hook: 'handleChoice', mastery: 10 }
    });
    const POINTS_PER_CHEST = 100;
    const BANK_LIMIT = 9;
    const EXCLUDED_ROUTES = Object.freeze(['learn-recorder']);
    const SKIN_NAMES = {
        quizzes: ['Scholar frame', 'Starlight answers', 'Aurora answers'], exam: ['Exam folio', 'Gilded folio', 'Aurora folio'],
        flashcards: ['Sage card backs', 'Foil deck edge', 'Aurora card backs'],
        guide: ['Sage highlighter', 'Gilded page edge', 'Aurora section stamps'],
        clinic: ['Sage clipboard', 'Attending pen', 'Aurora case stamps'],
        occlusion: ['Woven masks', 'Gilded reveal', 'Aurora ink reveal'],
        guided: ['Sage pointer', 'Gilded captions', 'Aurora captions']
    };
    const STUDY_COSMETICS = Object.freeze(Object.keys(GAME_MODE_SOURCES).flatMap(mode => ['rare', 'legendary', 'epic'].map((rarity, index) => Object.freeze({
        id: `${mode}-${rarity}`, mode, rarity, name: SKIN_NAMES[mode][index], cost: [160, 600, 2400][index],
        description: `${GAME_MODE_SOURCES[mode].label} appearance`, modes: [mode], loot: false
    }))));
    // Saturday and Sunday on the device's own calendar double study XP. Wallet Mementos follow
    // that XP, so the wallet never applies a second factor; fixed rewards and chest points stay 1x.
    const WEEKEND_STUDY_MULTIPLIER = 2;
    const studyRewardMultiplier = (date = new Date()) => [0, 6].includes(date.getDay()) ? WEEKEND_STUDY_MULTIPLIER : 1;
    const dayKey = () => new Date().toDateString();
    const count = value => Math.max(0, Math.floor(Number(value) || 0));
    const modeKey = value => Object.hasOwn(GAME_MODE_SOURCES, value) ? value : 'quizzes';

    function normalizeLedger(raw = {}, legacy = {}) {
        raw = raw && typeof raw === 'object' ? raw : {};
        if (raw.version != null && ![1, 2].includes(raw.version)) throw new Error('This reward ledger needs a newer app.');
        const points = raw.version === 2 ? Math.min(99, count(raw.points)) : Math.min(9, count(raw.correct)) * 10;
        const seen = new Set();
        const bank = (Array.isArray(raw.bank) ? raw.bank : Array.from({ length: Math.min(BANK_LIMIT, count(legacy.bank)) }, () => ({ id: crypto.randomUUID() })))
            .filter(chest => {
                if (!chest || typeof chest.id !== 'string' || !chest.id || seen.has(chest.id)) return false;
                seen.add(chest.id); return true;
            }).map(chest => ({ id: chest.id, rewardId: typeof chest.rewardId === 'string' ? chest.rewardId : null, mode: modeKey(chest.mode) }));
        let remaining = points;
        const pointsByMode = {};
        for (const mode of Object.keys(GAME_MODE_SOURCES)) {
            const amount = Math.min(remaining, count(raw.pointsByMode?.[mode]));
            if (amount) { pointsByMode[mode] = amount; remaining -= amount; }
        }
        if (remaining) pointsByMode.quizzes = (pointsByMode.quizzes || 0) + remaining;
        return { version: 2, revision: count(raw.revision), points, correct: Math.floor(points / 10), pointsByMode,
            bank, chestsSinceEpic: Math.min(2, count(raw.chestsSinceEpic ?? legacy.pity)),
            credited: { day: typeof raw.credited?.day === 'string' ? raw.credited.day : '', keys: [...new Set((raw.credited?.keys || []).filter(key => typeof key === 'string'))] },
            tally: Object.fromEntries(Object.entries(raw.tally || {}).slice(-14)),
            mastery: Object.fromEntries(Object.keys(GAME_MODE_SOURCES).map(mode => [mode, count(raw.mastery?.[mode])])),
            activity: Object.fromEntries(Object.keys(GAME_MODE_SOURCES).map(mode => [mode, count(raw.activity?.[mode])])),
            completions: Object.fromEntries(Object.keys(GAME_MODE_SOURCES).map(mode => [mode, count(raw.completions?.[mode])])) };
    }

    function creditStep(raw, step, day = dayKey()) {
        const ledger = normalizeLedger(raw);
        if (!step || !Object.hasOwn(GAME_MODE_SOURCES, step.mode) || typeof step.key !== 'string' || !step.key || !Number.isInteger(step.points) || step.points <= 0 || step.points > 100) return { ledger, accepted: false };
        if (ledger.credited.day !== day) ledger.credited = { day, keys: [] };
        if (ledger.credited.keys.includes(step.key)) return { ledger, accepted: false };
        ledger.credited.keys.push(step.key);
        const total = Math.floor(step.points);
        let remaining = total;
        while (remaining > 0) {
            const amount = Math.min(POINTS_PER_CHEST - ledger.points, remaining);
            ledger.points += amount;
            ledger.pointsByMode[step.mode] = (ledger.pointsByMode[step.mode] || 0) + amount;
            remaining -= amount;
            if (ledger.points === POINTS_PER_CHEST) {
                // Registry order breaks equal-contribution ties consistently across tabs.
                const mode = Object.keys(GAME_MODE_SOURCES).reduce((best, next) => (ledger.pointsByMode[next] || 0) > (ledger.pointsByMode[best] || 0) ? next : best, 'quizzes');
                ledger.bank.push({ id: crypto.randomUUID(), rewardId: null, mode });
                ledger.points = 0; ledger.pointsByMode = {};
            }
        }
        ledger.correct = Math.floor(ledger.points / 10);
        ledger.tally[day] = { ...ledger.tally[day], [step.mode]: count(ledger.tally[day]?.[step.mode]) + total };
        ledger.tally = Object.fromEntries(Object.entries(ledger.tally).slice(-14));
        if (step.mastery === true) ledger.mastery[step.mode]++;
        ledger.activity[step.mode]++;
        if (step.completed === true) ledger.completions[step.mode]++;
        ledger.revision++;
        return { ledger, accepted: true };
    }

    function identity(question) {
        const answerKey = typeof buildGameModeAnswerRewardIdentity === 'function' ? buildGameModeAnswerRewardIdentity(question)?.key : '';
        if (answerKey) return answerKey;
        const clean = value => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const front = clean(question?.q || question?.question || question?.front);
        const back = clean(question?.answerText || question?.answer || question?.back);
        return front && back ? `card-v1:${JSON.stringify([front, back])}` : '';
    }

    function credit(mode, key, unit = 'study', mastery = false, completed = false) {
        const source = GAME_MODE_SOURCES[mode];
        if (!source || !key || typeof isGameModeRewardEligible !== 'function' || !isGameModeRewardEligible()) return Promise.resolve(null);
        const multiplier = mode === 'clinic' && unit === 'complete' ? 2 : mode === 'guide' && unit === 'reading' ? 0.2 : 1;
        const points = source.points * multiplier;
        return awardXP(source.xp * multiplier, source.label, { quiet: true, gameStep: { mode, key: `${mode}:${unit}:${key}`, points, mastery, completed: completed || unit === 'complete' } });
    }

    function creditQuestion(mode, question, correct) {
        if (!isGameModeRewardEligible() || !isQuestionAnswerKeyVerified(question)) return Promise.resolve(null);
        if (!correct && !['quizzes', 'exam'].includes(mode)) return Promise.resolve(null);
        const answerReward = { ...buildGameModeAnswerRewardIdentity(question), day: dayKey(), correct: correct === true, mode };
        if (!answerReward.key) return Promise.resolve(null);
        if (!correct) return commitXPRecordChange({ answerReward });
        return awardXP(15, GAME_MODE_SOURCES[mode].label, { quiet: true, answerReward,
            gameStep: { mode, key: answerReward.key, points: 10, mastery: true } });
    }

    function masteryCount(record) {
        return count(record?.masteryQuizzes?.count) + Object.entries(GAME_MODE_SOURCES).reduce((sum, [mode, source]) => sum + (source.mastery ? Math.floor(count(record?.gamePath?.mastery?.[mode]) / source.mastery) : 0), 0);
    }

    function badgeThreshold(id) {
        return ({ 'getting-started': 1, '100-questions': 3, 'streak-25': 3, 'week-warrior': 7, 'apex-ascendant': 50 })[id] || Number(/^mastery-(\d+)$/.exec(id)?.[1]) || Infinity;
    }

    function progressLabel(mode, points) {
        const source = GAME_MODE_SOURCES[mode] || GAME_MODE_SOURCES.quizzes;
        return `About ${Math.ceil((POINTS_PER_CHEST - points) / source.points)} more ${source.unit}`;
    }

    function rewardAllowed(reward, mode) {
        const modes = reward.modes || (['shield', 'growth_boost'].includes(reward.effect?.type) ? ['quizzes', 'exam'] : ['all']);
        return modes.includes('all') || modes.includes(mode);
    }

    function normalizeCosmetics(raw = {}) {
        const owned = [...new Set((raw?.owned || []).filter(id => STUDY_COSMETICS.some(item => item.id === id)))];
        const equipped = Object.fromEntries(Object.entries(raw?.equipped || {}).filter(([mode, id]) => owned.includes(id) && STUDY_COSMETICS.some(item => item.id === id && item.mode === mode)));
        return { owned, equipped };
    }

    function wardrobeMarkup(raw, confirmId = '', enabled = true, balance = 0, pending = false) {
        const value = normalizeCosmetics(raw);
        return '<p>Optional visual styles for your study modes, available with Mementos.</p><div class="mc-shop-grid">' + STUDY_COSMETICS.map(item => {
            const owned = value.owned.includes(item.id), equipped = value.equipped[item.mode] === item.id;
            const confirming = confirmId === `study-buy:${item.id}`;
            return `<article class="mc-shop-card" data-card="${item.id}" tabindex="-1"><span class="mc-tier">${item.rarity}</span><div class="gp-skin-preview is-${item.rarity}" aria-hidden="true">Aa</div><h3>${item.name}</h3><p>${item.description}</p><div class="mc-price">${owned ? 'Collected' : `${item.cost} Mementos`}</div><button type="button" data-shop="${owned ? equipped ? 'study-default' : 'study-equip' : 'study-buy'}" data-id="${item.id}"${pending || !owned && (!enabled || Number(balance) < item.cost) ? ' disabled' : ''}>${owned ? equipped ? 'Use default' : 'Equip' : confirming ? 'Confirm purchase' : 'Buy'}</button></article>`;
        }).join('') + '</div>';
    }

    const readingSections = new Map();
    const pendingReading = new Set();
    let lastActivity = 0;
    let lastTick = 0;
    let readingObserver = null;
    function trackSection(group, section) {
        if (!group || !section?.id || !section.items?.length || typeof IntersectionObserver !== 'function') return;
        const text = section.items.map(node => node.textContent || '').join(' ').trim();
        const words = text.split(/\s+/).filter(Boolean).length;
        const source = typeof pdfTutorState !== 'undefined' ? pdfTutorState.pdfName || '' : '';
        if (!source || !words) return;
        const key = JSON.stringify([source, section.id, text]);
        if (!readingObserver) readingObserver = new IntersectionObserver(entries => { for (const entry of entries) { const record = readingSections.get(entry.target); if (record) { if (entry.isIntersecting) record.visibleNodes.add(entry.target); else record.visibleNodes.delete(entry.target); record.visible = record.visibleNodes.size > 0; } } });
        const targets = section.items.filter(node => typeof node.getClientRects === 'function');
        if (!targets.length) targets.push(group);
        const record = targets.map(node => readingSections.get(node)).find(value => value?.key === key) || { key, cap: Math.floor(words / 36), visible: false, visibleNodes: new Set() };
        for (const target of targets) { if (readingSections.get(target) === record) continue; readingSections.set(target, record); readingObserver.observe(target); }
    }

    function readingTick(now = performance.now()) {
        const elapsed = Math.min(5000, Math.max(0, now - lastTick)); lastTick = now;
        // Prune before the eligibility return: re-rendered guides otherwise stay pinned here while idle or with Game Mode off.
        for (const [group] of readingSections) if (!group.isConnected) { readingObserver?.unobserve(group); readingSections.delete(group); }
        if (document.hidden || !document.hasFocus() || now - lastActivity >= 45000 || !isGameModeRewardEligible()) return;
        const entry = [...readingSections.entries()].find(([group, record]) => record.visible && record.cap > 0 && !(record.day === dayKey() && record.elapsed >= record.cap * 12000) && group.getClientRects().length && !group.closest('[inert], [hidden], .is-collapsed'));
        if (!entry) return;
        const record = entry[1];
        // Twelve seconds = two points. Stable slot receipts cap reading and dedupe tabs.
        const day = dayKey();
        const saved = typeof xpData !== 'undefined' ? xpData.gamePath?.credited : null;
        const paid = saved?.day === day ? saved.keys.filter(key => key.startsWith(`guide:reading:${record.key}:`)).length : 0;
        const prior = record.day === day ? Math.max(record.elapsed || 0, paid * 12000) : paid * 12000;
        const next = Math.min(record.cap * 12000, prior + elapsed);
        record.elapsed = next; record.day = day;
        for (let slot = 0; slot < Math.floor(next / 12000); slot++) {
            const receiptKey = `guide:reading:${record.key}:${slot}`;
            const ledger = typeof xpData !== 'undefined' ? xpData.gamePath : null;
            if (ledger?.credited?.day === dayKey() && ledger.credited.keys.includes(receiptKey)) continue;
            if (pendingReading.has(receiptKey)) continue;
            pendingReading.add(receiptKey);
            void Promise.resolve(credit('guide', `${record.key}:${slot}`, 'reading')).finally(() => pendingReading.delete(receiptKey));
        }
    }

    function syncSurfaces() {
        if (!window.memoraGamePath) return;
        const mount = (selector, mode) => {
            for (const node of document.querySelectorAll(selector)) if (node.getClientRects().length) window.memoraGamePath.mount(node, mode);
        };
        // Quiz headers are created after initial boot and can be replaced on navigation.
        // Mount/render the canonical Quiz chip here as well as on XP updates.
        window.memoraGamePath.render();
        mount('.exam-header', 'exam');
        mount('#clinic-modal .modal-header, #clinic-modal .clinic-header', 'clinic');
        let tutorChipShown = false;
        if (typeof pdfTutorState !== 'undefined') {
            const mode = pdfTutorState.occlusion?.active ? 'occlusion' : { guide: 'guide', flashcards: 'flashcards' }[pdfTutorState.tutorViewMode] || 'guided';
            mount('#pdf-header', mode);
            const tutorChip = document.querySelector('#pdf-header .memora-game-path');
            if (tutorChip) { tutorChip.dataset.gameInactive = String(!document.body.classList.contains('pdf-tutor-mode')); tutorChip.hidden = !isGameModeRewardEligible() || tutorChip.dataset.gameInactive === 'true'; }
            tutorChipShown = !!tutorChip && !tutorChip.hidden && !!tutorChip.getClientRects?.().length;
        }
        // The Tutor header's top-left chip is the one progress chip a Tutor module shows;
        // the module's own header carries one only when the Tutor header has none.
        if (tutorChipShown) document.querySelectorAll('#flashcard-container .fc-controls .memora-game-path, .pdf-study-guide-header .memora-game-path').forEach(chip => chip.remove());
        else {
            mount('#flashcard-container .fc-controls', 'flashcards');
            mount('.pdf-study-guide-header', 'guide');
        }
        // Study styles left the Marketplace; clear any skin bought earlier so nothing stays stuck on.
        for (const mode of Object.keys(GAME_MODE_SOURCES)) {
            const attribute = `data-memora-${mode}-skin`;
            if (document.body.getAttribute(attribute)) document.body.setAttribute(attribute, '');
        }
    }

    function boot() {
        // Only real input counts as presence: scroll also fires for app-driven scrolling (voice
        // follow-along, restores), and leaving the tab or window drops presence until the next
        // input, so an unattended Study Guide never keeps earning.
        const activity = event => { if (!event || event.isTrusted !== false) lastActivity = performance.now(); };
        for (const event of ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart']) document.addEventListener(event, activity, { passive: true, capture: true });
        const away = () => { lastActivity = -Infinity; };
        document.addEventListener('visibilitychange', () => { if (document.hidden) away(); });
        window.addEventListener('blur', away);
        window.addEventListener('memora:game-mode-changed', () => { activity(); syncSurfaces(); });
        // Ignore chip rendering itself; only new native header roots need mounting.
        let queued = false;
        new MutationObserver(records => {
            if (!records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && !node.closest?.('.memora-game-path, .gp-box-overlay')))) return;
            if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; syncSurfaces(); }); }
        }).observe(document.body, { childList: true, subtree: true });
        lastTick = performance.now();
        setInterval(() => { readingTick(); syncSurfaces(); }, 5000);
        syncSurfaces();
    }

    window.memoraGameStudy = Object.freeze({ sources: GAME_MODE_SOURCES, excludedRoutes: EXCLUDED_ROUTES,
        normalizeLedger, creditStep, credit, creditQuestion, identity, masteryCount, badgeThreshold, progressLabel, rewardAllowed,
        cosmetics: STUDY_COSMETICS, normalizeCosmetics, wardrobeMarkup, trackSection, readingTick, syncSurfaces, pointsPerChest: POINTS_PER_CHEST, studyRewardMultiplier });
    window.memoraGameCredit = credit;
    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
    }
})();
